import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import * as d3 from 'd3';
import { Stack } from 'd3';
import React from 'react';
import { theme } from '../../../theme/theme';

const useBarChartClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        gap: 15,
        backgroundColor: 'white',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 20,
        color: theme.colors.neutral[2],
        ...theme.fonts.h6,
    },
    legend: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 20,
        color: theme.colors.neutral[2],
        ...theme.fonts.h6,
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        '& > div': {
            width: 16,
            height: 16,
            borderRadius: 2,
        },
    },
    chart: {
        position: 'relative',
        flex: 1,
        '& > svg': {
            position: 'absolute',
            width: '100%',
            height: '100%',
        },
    },
    xAxis: {
        '& > .domain': { stroke: theme.colors.neutral[6] },
        '& text': {
            color: theme.colors.neutral[2],
            ...theme.fonts.caption,
        },
    },
    yAxis: {
        '& > .domain': { stroke: 'transparent' },
        '& line': { stroke: theme.colors.neutral[6] },
        '& text': {
            color: theme.colors.neutral[2],
            ...theme.fonts.caption,
        },
    },
    data: {
        '& rect': {
            rx: 2,
            ry: 2,
        },
    },
});

type MouseEvents =
    | 'mousedown'
    | 'mouseup'
    | 'click'
    | 'dblclick'
    | 'mouseover'
    | 'mouseout'
    | 'mouseenter'
    | 'mouseleave';

/**
 * Bar chart with support fot grouped and stacked layouts.
 *
 * @param type chart type
 * @param index x axis values
 * @param series array containing all series to be displayed in the chart
 * @param range minimum and maximum values for the y axis
 * @param events events to be attached to the chart bars
 * @param rootProps remaining props to be passed to the root element
 */
export const BarChart = ({
    type,
    title,
    index,
    series,
    range = [0, 10],
    events,
    ...rootProps
}: {
    type: 'grouped' | 'stacked';
    title: string;
    index: string[];
    series: { label?: string; color: string[]; values: number[] }[];
    range?: [number, number];
    events?: Partial<{ [event in MouseEvents]: (event: MouseEvent) => void }>;
} & JSX.IntrinsicElements['div']) => {
    const classes = useBarChartClasses();
    const svg$ = React.useRef<SVGSVGElement>();

    React.useLayoutEffect(() => {
        if (!svg$.current) return;
        render();
        let size = { x: svg$.current.clientWidth, y: svg$.current.clientHeight };
        const interval = setInterval(() => {
            if (svg$.current?.clientWidth === size.x && svg$.current?.clientHeight === size.y) return;
            size = { x: svg$.current?.clientWidth ?? 0, y: svg$.current?.clientHeight ?? 0 };
            render();
        }, 500);
        return () => clearInterval(interval);
    }, [svg$.current, type, index, series, range, events]);

    const render = () => {
        if (!svg$.current) return;

        // data processing
        const stack = ((d3.stack() as unknown) as Stack<any, unknown, number>)
            .keys(d3.range(series.length))
            .value((_, key, i) => series[key].values[i] ?? 0)(index)
            .map((data, i) => data.map(([y0, y1]) => [y0, y1, i]));

        const groupedExtent = d3
            .extent([...range, ...d3.extent(series.flatMap(s => s.values))].map(v => v ?? 0))
            .map((v: number | undefined) => v ?? 0);
        const stackedExtent = d3
            .extent([...range, ...stack.flat().flatMap(([y0, y1, k]) => [y0, y1])])
            .map((v: number | undefined) => v ?? 0);
        const shift = Math.max(
            Math.floor(Math.min(groupedExtent[0], stackedExtent[0])).toString().length,
            Math.ceil(Math.max(groupedExtent[1], stackedExtent[1])).toString().length,
        );

        const size = { x: svg$.current.clientWidth, y: svg$.current.clientHeight };
        const margin = { left: (shift + 1) * 8, right: 0, top: title != undefined ? 40 : 20, bottom: 20 };
        const box = { x: size.x - margin.left - margin.right, y: size.y - margin.top - margin.bottom };

        // root setup
        const svg = d3
            .select(svg$.current)
            .attr('viewBox', `0 0 ${size.x} ${size.y}`)
            .attr('preserveAspectRatio', 'none');

        // scales setup
        const x = d3
            .scaleBand<number>()
            .domain(d3.range(index.length))
            .range([margin.left, size.x - margin.right])
            .paddingInner(0.3)
            .paddingOuter(0.4);
        const y = d3.scaleLinear().range([size.y - margin.bottom, margin.top]);
        const xg = d3 //
            .scaleBand<number>()
            .domain(d3.range(series.length))
            .range([0, x.bandwidth()]);

        const drawAxis = (x: d3.ScaleBand<number>, y: d3.ScaleLinear<number, number, never>) => {
            const xAxis = d3 //
                .axisBottom(x)
                .tickSize(0)
                .tickFormat(i => index[i])
                .tickPadding(5);
            const yAxis = d3
                .axisLeft(y)
                .tickSize(-size.x + (margin.left + margin.right))
                .tickArguments([Math.max(2, Math.floor(box.y / 40))])
                .tickPadding(5);
            svg.selectAll('g.x')
                .data(['x'])
                .join('g')
                .classed(clsx('x', classes.xAxis), true)
                .call(xAxis as any)
                .attr('transform', `translate(0, ${size.y - margin.bottom})`);
            svg.selectAll('g.y')
                .data(['y'])
                .join('g')
                .classed(clsx('y', classes.yAxis), true)
                .transition()
                .duration(500)
                .call(yAxis as any)
                .attr('transform', `translate(${margin.left}, 0)`);
        };

        const drawBase = (x: d3.ScaleBand<number>, y: d3.ScaleLinear<number, number, never>) => {
            svg.selectAll('defs')
                .data([''])
                .join('defs')
                .selectAll('linearGradient')
                .data(stack)
                .join('linearGradient')
                .attr('id', (_, i) => `lg-${i}`)
                .attr('gradientTransform', 'rotate(90)')
                .selectAll('stop')
                .data((_, i) => [series[i].color[0], series[i].color[1 % series[i].color.length]] as string[])
                .join('stop')
                .attr('stop-color', c => c)
                .attr('offset', (_, i) => `${i * 100}%`);
            const rects = svg
                .selectAll('g.data')
                .data(stack)
                .join('g')
                .classed(clsx('data', classes.data), true)
                .style('fill', (_, i) => `url(#lg-${i})`)
                .selectAll('rect')
                .data(d => d)
                .join('rect')
                .attr('x', (_, i) => x(i)!)
                .attr('y', size.y - margin.bottom)
                .attr('width', x.bandwidth())
                .attr('height', 0)
                .attr('data-index', (_, i) => i)
                .attr('data-series', d => d[2]);
            Object.entries(events ?? {}).forEach(([event, callback]) => rects.on(event, callback!));
            return rects;
        };

        const grouped = () => {
            y.domain(groupedExtent);
            drawAxis(x, y);
            drawBase(x, y)
                .attr('x', ([, , k], i) => x(i)! + xg(k)!)
                .attr('y', ([y0, y1]) => y(y1 - y0))
                .attr('width', xg.bandwidth())
                .attr('height', ([y0, y1]) => y(0) - y(y1 - y0));
        };

        const stacked = () => {
            y.domain(stackedExtent);
            drawAxis(x, y);
            drawBase(x, y)
                .attr('x', (_, i) => x(i)!)
                .attr('y', ([_, y1]) => y(y1))
                .attr('width', x.bandwidth())
                .attr('height', ([y0, y1]) => y(0) - y(y1 - y0) + 1);
        };

        type === 'grouped' ? grouped() : stacked();
    };

    return (
        <div {...rootProps} className={clsx(classes.root, rootProps.className)}>
            <div className={classes.header}>
                <span>{title}</span>
                <div className={classes.legend}>
                    {series.map(s => {
                        const background = `linear-gradient(${s.color[0]}, ${s.color[1 % s.color.length]})`;
                        return (
                            <div className={classes.legendItem}>
                                <div style={{ background }} />
                                <span>{s.label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={classes.chart}>
                <svg ref={svg$ as any} />
            </div>
        </div>
    );
};
